import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, X, Key, AlertTriangle, CheckCircle2, 
  ExternalLink, Ship, Newspaper, Globe, Copy, Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const APIConfigPanel = ({ token, isOpen, onClose }) => {
  const [aisStatus, setAisStatus] = useState(null);
  const [newsStatus, setNewsStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && token) {
      fetchStatuses();
    }
  }, [isOpen, token]);

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const [aisRes, newsRes] = await Promise.all([
        axios.get(`${API_URL}/api/ais/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/news/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAisStatus(aisRes.data.ais);
      setNewsStatus(newsRes.data.scraper);
    } catch (err) {
      console.error('Error fetching API status:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const apiConfigs = [
    {
      id: 'aisstream',
      name: 'AISStream.io',
      category: 'AIS Maritime (temps réel)',
      icon: Ship,
      envVar: 'AISSTREAM_API_KEY',
      configured: aisStatus?.providers?.find(p => p.name === 'AISStream')?.configured || false,
      active: aisStatus?.active_provider === 'AISStream',
      pricing: [
        { plan: 'Basic', price: 'GRATUIT', details: 'WebSocket temps réel, recherche par zone' }
      ],
      url: 'https://aisstream.io',
      recommended: true,
      note: 'API gratuite avec données temps réel via WebSocket - Recommandé pour le CESM'
    },
    {
      id: 'marinetraffic',
      name: 'MarineTraffic / Kpler',
      category: 'AIS Maritime (Enterprise)',
      icon: Ship,
      envVar: 'MARINETRAFFIC_API_KEY',
      configured: aisStatus?.providers?.find(p => p.name === 'MarineTraffic')?.configured || false,
      pricing: [
        { plan: 'Enterprise', price: 'Sur devis', details: 'Données complètes, historique, satellite' }
      ],
      url: 'https://www.kpler.com/maritime-intelligence',
      recommended: false,
      note: 'Kpler a racheté MarineTraffic - Solutions enterprise uniquement'
    },
    {
      id: 'rapidapi',
      name: 'RapidAPI VesselFinder',
      category: 'AIS Maritime (limité)',
      icon: Ship,
      envVar: 'RAPIDAPI_KEY',
      configured: aisStatus?.providers?.find(p => p.name === 'RapidAPI-VesselFinder')?.configured || false,
      pricing: [
        { plan: 'Basic', price: 'GRATUIT', details: 'Recherche par MMSI uniquement' }
      ],
      url: 'https://rapidapi.com/lecongphu/api/vesselfinder1',
      recommended: false,
      note: 'Ne permet PAS la recherche par zone - utilité limitée pour le CESM'
    },
    {
      id: 'aishub',
      name: 'AISHub',
      category: 'AIS Maritime',
      icon: Ship,
      envVar: 'AISHUB_USERNAME',
      configured: aisStatus?.providers?.find(p => p.name === 'AISHub')?.configured || false,
      pricing: [
        { plan: 'Community', price: 'GRATUIT', details: 'Nécessite de partager une station AIS' }
      ],
      url: 'https://www.aishub.net/',
      recommended: false,
      note: 'Gratuit si vous avez une station AIS à partager'
    }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-slate-900 border border-amber-500/30 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="api-config-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-amber-500/10">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6 text-amber-400" />
              <div>
                <h2 className="font-mono font-bold text-amber-400 text-lg">CONFIGURATION DES APIs</h2>
                <p className="text-xs text-slate-400">
                  APIs nécessitant une clé pour activer les données réelles
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Status Summary */}
            <div className="mb-6 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-5 h-5 text-cyan-400" />
                <span className="font-mono text-cyan-400 text-sm font-bold">STATUT ACTUEL</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Ship className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-white">Données AIS:</span>
                  <span className={`text-sm font-bold ${aisStatus?.is_live ? 'text-green-400' : 'text-amber-400'}`}>
                    {aisStatus?.is_live ? `LIVE (${aisStatus?.active_provider})` : 'SIMULATION'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Newspaper className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-white">News Scraper:</span>
                  <span className="text-sm font-bold text-green-400">
                    LIVE ({newsStatus?.sources_configured || 0} sources)
                  </span>
                </div>
              </div>
            </div>

            {/* Info Banner - Conditional based on AIS status */}
            {aisStatus?.is_live ? (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/50 rounded-lg flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-mono text-green-400 text-sm font-bold mb-1">
                    DONNÉES AIS EN MODE LIVE
                  </p>
                  <p className="text-sm text-white/80">
                    Le provider <strong>{aisStatus?.active_provider}</strong> est actif. Les données maritimes sont 
                    récupérées en temps réel. Note: En environnement preview, les connexions WebSocket peuvent 
                    être limitées - le système bascule alors automatiquement vers la simulation.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-mono text-amber-400 text-sm font-bold mb-1">
                    DONNÉES AIS EN MODE SIMULATION
                  </p>
                  <p className="text-sm text-white/80">
                    Les données maritimes (positions des pétroliers, densité de trafic) sont actuellement simulées. 
                    Pour obtenir des données réelles, configurez l'une des clés API ci-dessous.
                  </p>
                </div>
              </div>
            )}

            {/* API Cards */}
            <div className="space-y-4">
              {apiConfigs.map((api) => {
                const Icon = api.icon;
                return (
                  <div 
                    key={api.id}
                    className={`p-4 border rounded-lg ${
                      api.configured 
                        ? 'border-green-500/50 bg-green-500/5' 
                        : api.recommended 
                          ? 'border-cyan-500/50 bg-cyan-500/5' 
                          : 'border-slate-700 bg-slate-800/30'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Icon className={`w-6 h-6 ${api.configured ? 'text-green-400' : api.recommended ? 'text-cyan-400' : 'text-slate-400'}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-white">{api.name}</span>
                            {api.recommended && !api.configured && (
                              <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs font-mono rounded">
                                RECOMMANDÉ
                              </span>
                            )}
                            {api.configured && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-mono rounded flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> CONFIGURÉ
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">{api.category}</span>
                        </div>
                      </div>
                      <a 
                        href={api.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs font-mono rounded flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> Site
                      </a>
                    </div>

                    {/* Pricing Table */}
                    <div className="mb-3">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-slate-400 text-xs">
                            <th className="text-left py-1">Plan</th>
                            <th className="text-left py-1">Prix</th>
                            <th className="text-left py-1">Détails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {api.pricing.map((plan, idx) => (
                            <tr key={idx} className="border-t border-slate-700/50">
                              <td className="py-1.5 text-white">{plan.plan}</td>
                              <td className={`py-1.5 font-bold ${plan.price === 'GRATUIT' ? 'text-green-400' : 'text-amber-400'}`}>
                                {plan.price}
                              </td>
                              <td className="py-1.5 text-slate-400 text-xs">{plan.details}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Environment Variable */}
                    <div className="flex items-center gap-2 p-2 bg-black/30 rounded">
                      <span className="text-xs text-slate-400">Variable:</span>
                      <code className="text-xs text-cyan-400 font-mono flex-1">{api.envVar}</code>
                      <button 
                        onClick={() => copyToClipboard(api.envVar)}
                        className="p-1 hover:bg-slate-700 rounded"
                        title="Copier"
                      >
                        <Copy className="w-3 h-3 text-slate-400" />
                      </button>
                    </div>
                    
                    {/* Note */}
                    {api.note && (
                      <div className={`mt-2 p-2 rounded text-xs ${api.note.includes('⚠️') ? 'bg-amber-500/10 text-amber-400' : 'bg-slate-700/50 text-slate-300'}`}>
                        {api.note}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Instructions */}
            <div className="mt-6 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
              <h3 className="font-mono text-cyan-400 text-sm font-bold mb-3">
                INSTRUCTIONS DE CONFIGURATION
              </h3>
              <ol className="space-y-2 text-sm text-white/80 list-decimal list-inside">
                <li>Créez un compte sur le site du provider choisi</li>
                <li>Obtenez votre clé API (gratuite pour VesselFinder/RapidAPI)</li>
                <li>Ajoutez la variable d'environnement dans le fichier <code className="text-cyan-400">/app/backend/.env</code></li>
                <li>Redémarrez le backend pour activer les données réelles</li>
              </ol>
              <div className="mt-3 p-2 bg-black/30 rounded">
                <p className="text-xs text-slate-400 mb-1">Exemple pour VesselFinder:</p>
                <code className="text-xs text-green-400 font-mono">VESSELFINDER_API_KEY=votre_clé_rapidapi</code>
              </div>
            </div>

            {/* Message for Admiral */}
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/50 rounded-lg">
              <h3 className="font-mono text-blue-400 text-sm font-bold mb-2">
                NOTE TECHNIQUE
              </h3>
              <div className="text-sm text-white/80 bg-black/30 p-3 rounded font-mono">
                <p><strong>Configuration actuelle:</strong></p>
                <p className="mt-2">• AISStream.io est configuré avec votre clé API</p>
                <p>• Le système utilise des données AIS en temps réel via WebSocket</p>
                <p className="mt-2"><strong>Limitation environnement preview:</strong></p>
                <p className="mt-1">Les connexions WebSocket sortantes peuvent être bloquées par le proxy de l'environnement de développement. Dans ce cas, le système bascule automatiquement vers la simulation.</p>
                <p className="mt-2"><strong>En production:</strong> Les connexions WebSocket fonctionneront normalement et les données AIS seront en temps réel.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default APIConfigPanel;
